import { useMutation } from '@apollo/client';
import type { UpdateImpactMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { UpdateImpactDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import type { UpdateImpactTRPCInput } from './useUpdateImpactTRPC';
import { useUpdateImpactTRPC } from './useUpdateImpactTRPC';

export const useUpdateImpact = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  // GraphQL mutation
  const [updateImpactGraphQL, graphqlState] = useMutation(
    UpdateImpactDocument,
    {
      update: (cache) => {
        evictField(cache, 'impact');
        evictField(cache, 'impact_rating');
      },
    }
  );

  // tRPC mutation
  const trpcMutation = useUpdateImpactTRPC();

  const updateImpact = async (
    variables: UpdateImpactTRPCInput
  ): Promise<UpdateImpactMutation> => {
    if (trpcEnabled) {
      return trpcMutation.updateImpact(variables);
    }

    const result = await updateImpactGraphQL({
      variables: {
        object: variables,
      },
    });
    if (!result.data) {
      throw new Error('Failed to update impact');
    }

    return result.data;
  };

  // Return appropriate state based on feature flag
  if (trpcEnabled) {
    return {
      updateImpact,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    updateImpact,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
